import storeHelper from "@/store/store-helper";
import { ActionResult } from "@/todos";
import { Controllers, IsBadRequestResult, IsNotFoundResult, IsServerErrorResult, ProblemResult } from "@infoland/iprova-api";
import { ReadConfirmation } from "../../../types/readconfirmation/readconfirmation";

export interface ReadListState {
    list: ReadConfirmation[];
    lastConfirmed: ConfirmationParameters;
}

export function initialize(): ReadListState {
    return {
        list: null,
        lastConfirmed: null
    };
}

//initial state
const state: ReadListState = initialize();

const getters = {
    /**
     * Gets the list of read confirmations
     * @param state
     */
    list(state: ReadListState): ReadConfirmation[] {
        return state.list;
    },

    /**
     * Gets ID of the last confirmed read confirmation
     * @param state
     */
    lastConfirmed(state: ReadListState): ConfirmationParameters {
        return state.lastConfirmed;
    }
};

const mutations = {
    /**
     * Sets the list of read confirmations
     * @param state
     * @param readList
     */
    list(state: ReadListState, readList: ReadConfirmation[]): void {
        state.list = readList;
        //new list => reset the last read item ID
        state.lastConfirmed = null;
    },

    /**
     * Sets the ID of the last confirmed read confirmation
     * @param state
     * @param readConfirmationID ID of the read confirmation
     */
    lastConfirmed(state: ReadListState, confirmation: ConfirmationParameters): void {
        if (!state.list) return;

        if (confirmation == null || confirmation.read_confirmation_id == null || confirmation.document.document_id == null) return;

        //ensure it is a numeric value type
        state.lastConfirmed = confirmation;

        //find item
        let item: ReadConfirmation = state.list.find(rci => {
            return rci.read_confirmation_id == confirmation.read_confirmation_id && rci.document.document_id == confirmation.document.document_id;
        });

        //Update item state
        if (item) {
            item.confirmed = true;
        }
    }
};

const actions = {
    /**
     * Confirm reading
     * @param confirm The id of the document and read confirmation
     */
    async updateReadConfirmation({ commit }, confirm: ConfirmationParameters): Promise<ActionResult<void, string>> {
        let patchResult = await Controllers.GenericController.patchAsync<ConfirmationParameters, void | ProblemResult<string>>(
            `documents/read_confirmations/${confirm.read_confirmation_id}`,
            {
                document: {
                    document_id: confirm.document.document_id
                } as ReadConfirmationPatchDocument,
                reason_for_not_applicable: confirm.reason_for_not_applicable,
                confirmed: confirm.confirmed
            } as ConfirmationParameters
        );
        let result = ReadConfirmationResult(patchResult);

        if (result) commit("lastConfirmed", confirm);

        return result;
    },

    async loadListAsync({ commit }) {
        let readList: ReadConfirmation[] = await Controllers.GenericController.getAsync<ReadConfirmation[]>(`/documents/read_confirmations`);
        //sort array on publish date descending
        readList.sort(function (a, b) {
            let aNum = parseInt(a.document.published_datetime);
            let bNum = parseInt(b.document.published_datetime);

            let aHasDeadline = !!a.document.read_confirmation_deadline;
            let bHasDeadline = !!b.document.read_confirmation_deadline;

            if (aHasDeadline && !bHasDeadline) {
                return -1;
            }
            if (!aHasDeadline && bHasDeadline) {
                return 1;
            }
            if (a.document.read_confirmation_deadline !== b.document.read_confirmation_deadline) {
                return a.document.read_confirmation_deadline.localeCompare(b.document.read_confirmation_deadline);
            }
            return aNum - bNum;
        });

        commit("list", readList);
    }
};

function ReadConfirmationResult(result: void | ProblemResult<string>): ActionResult<void, string> {
    if (IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarServerErrorMessage(result);
        return { success: false };
    } else if (IsBadRequestResult(result) || IsNotFoundResult(result)) {
        if (result.extrainfo) {
            storeHelper.siteStore.setSnackBarMessageLookup("teams.errorMessages." + result.extrainfo, true);
        } else {
            storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        }

        return { success: false, errorData: result.extrainfo };
    } else return { success: true };
}

export interface ConfirmationParameters {
    read_confirmation_id: number;
    document: ReadConfirmationPatchDocument;
    reason_for_not_applicable: string;
    confirmed: boolean;
}

export interface ReadConfirmationPatchDocument {
    document_id: string;
}

// export the namespace
export const namespace = "readList";

// export the store 'module'
export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
